import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PublicSiteHeader from "../components/PublicSiteHeader";
import AppFooter from "../components/AppFooter";
import logo from "../assets/logo_basic.png";

const methodologySchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.reignitespirit.com/methodology#webpage",
  url: "https://www.reignitespirit.com/methodology",
  name: "Career Intelligence Methodology",
  description:
    "How Reignite Spirit evaluates career risk, transferable skills, career pathways and transition readiness.",
  isPartOf: {
    "@id": "https://www.reignitespirit.com/#website",
  },
};

const steps = [
  {
    number: "01",
    title: "Collect user information",
    text: "The process begins with information supplied by the user, such as current role, experience, skills, location, career preferences, financial situation and transition constraints.",
  },
  {
    number: "02",
    title: "Understand the current role",
    text: "The user’s role is assessed in terms of common activities, responsibilities, skill requirements and exposure to changing technology or automation.",
  },
  {
    number: "03",
    title: "Identify transferable skills",
    text: "Existing strengths are mapped to adjacent occupations and career pathways where similar capabilities may remain useful.",
  },
  {
    number: "04",
    title: "Evaluate career risk",
    text: "The platform considers task exposure, technology trends, role structure and the extent to which human judgement, relationships or physical presence remain important.",
  },
  {
    number: "05",
    title: "Explore localized pathways",
    text: "Potential pathways may be filtered or prioritized using location, market relevance, role availability, transition difficulty and user preferences.",
  },
  {
    number: "06",
    title: "Assess transition readiness",
    text: "Financial runway, learning requirements, timing, personal responsibilities and practical barriers may be considered when shaping recommendations.",
  },
];

export default function MethodologyPage() {
  return (
    <div className="min-h-screen bg-[#0F0A24] text-white flex flex-col">
      <SEO
        title="Career Intelligence Methodology"
        description="Understand how Reignite Spirit evaluates automation exposure, transferable skills, career pathways, local labour-market signals and transition readiness."
        path="/methodology"
        image="/og-methodology.jpg"
        type="website"
        author="Reignite Spirit"
        keywords="career methodology, career risk analysis, transferable skills, AI job risk, career pathway methodology"
        schema={methodologySchema}
      />

      <PublicSiteHeader />

      <main className="px-6 py-16 flex-1">
        <article className="max-w-5xl mx-auto">
          <header className="text-center mb-14">
            <p className="text-[#EB6AB5] font-bold uppercase tracking-[0.25em] text-sm">
              Our Methodology
            </p>

            <h1 className="text-4xl md:text-6xl font-extrabold mt-4">
              How career intelligence is produced
            </h1>

            <p className="mt-6 max-w-3xl mx-auto text-lg text-white/70 leading-relaxed">
              Reignite Spirit uses a structured decision-support approach to
              examine career exposure, transferable skills, transition options
              and readiness. The process is designed to provide useful
              direction, not certainty.
            </p>
          </header>

          <section className="space-y-5">
            {steps.map((step) => (
              <div
                key={step.number}
                className="rounded-3xl border border-white/10 bg-white/5 p-7 flex flex-col md:flex-row gap-5"
              >
                <div className="text-4xl font-extrabold text-[#EB6AB5]">
                  {step.number}
                </div>

                <div>
                  <h2 className="text-2xl font-bold">
                    {step.title}
                  </h2>

                  <p className="text-white/70 leading-relaxed mt-3">
                    {step.text}
                  </p>
                </div>
              </div>
            ))}
          </section>

          <section className="mt-10 rounded-3xl border border-white/10 bg-white/5 p-7 md:p-10">
            <h2 className="text-3xl font-bold mb-5">
              Factors that may influence recommendations
            </h2>

            <div className="grid sm:grid-cols-2 gap-4 text-white/70">
              <p>Current occupation and experience</p>
              <p>Transferable and adjacent skills</p>
              <p>Automation and AI exposure</p>
              <p>Location and labour-market context</p>
              <p>Training or qualification requirements</p>
              <p>Financial runway and monthly obligations</p>
              <p>Career preferences and work conditions</p>
              <p>Transition difficulty and expected timeframe</p>
            </div>
          </section>

          <section className="mt-8 rounded-3xl border border-amber-400/30 bg-amber-400/5 p-7">
            <h2 className="text-2xl font-bold text-amber-100 mb-4">
              Important methodology limitations
            </h2>

            <p className="text-amber-100/80 leading-relaxed">
              Career markets, technologies, salaries, legal conditions and
              hiring activity change over time. Data may be incomplete,
              delayed, generalized or unavailable for a particular location.
              Recommendations should therefore be treated as decision-support
              information rather than predictions or guaranteed outcomes.
            </p>
          </section>

          <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-7">
            <h2 className="text-2xl font-bold mb-4">
              Related information
            </h2>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/data-sources"
                className="px-5 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold"
              >
                View data sources
              </Link>

              <Link
                to="/disclaimer"
                className="px-5 py-3 rounded-full border border-white/15 hover:border-[#EB6AB5]"
              >
                Read the disclaimer
              </Link>

              <Link
                to="/editorial-policy"
                className="px-5 py-3 rounded-full border border-white/15 hover:border-[#EB6AB5]"
              >
                Editorial policy
              </Link>
            </div>
          </section>
        </article>
      </main>

      {/* =====================================================
          SAME FOOTER AS FAQ
      ===================================================== */}
      <AppFooter
        variant="landing"
        logoSrc={logo}
      />
    </div>
  );
}